import React, { useState, useEffect, useRef } from "react";
import { Box, Divider, Typography, CardMedia, Button, IconButton } from "@mui/material";
import { Form, FormikErrors, FormikTouched } from "formik";
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { BusinessGroup } from "../../../../types/models/BusinessGroup";
import { BeaconDialogModes } from "./BeaconDialog";
import { useAppSelector } from "../../../../utils/hooks";
import FileUploadPreview from "../../../../components/FileUploadPreview";
import { useDropzone } from "react-dropzone";
import { AppConstants } from "../../../../types/Constants";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import { convertByteString, getImageDimensions, getImageURLCategoryListIcon, getImageURLOnServerBeaconFace, resizeImage } from "../../../../utils/utils";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CategoryDropdown from "../../pages/categories/CategoryDropdown";
import { Category } from "../../../../types/models/Category";
import ImageLoader from "../../../../components/ImageLoader";
import UploadImageModal from "../../../../components/UploadImageModal";
import { Point } from "react-easy-crop";

interface BeaconFormProps {
  mode?: typeof BeaconDialogModes[number],
  values: MakeBeacon,
  errors: FormikErrors<MakeBeacon>,
  touched: FormikTouched<MakeBeacon>,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>
  groups: Array<BusinessGroup>
  imageId?: string,
  handleChange: (e: React.ChangeEvent<any>) => void,
  handleDeleteFace: (deleteFaceImage: boolean) => void,
  handleFileChange: (file: File | undefined) => void,
  onCategorySelect: (category: Category) => void;
}

const BeaconForm: React.FC<BeaconFormProps> = ({ mode, values, errors, handleChange, touched, setFieldValue, groups, imageId, handleFileChange, handleDeleteFace, onCategorySelect }) => {

  const categoriesGroupState = useAppSelector((state) => state.category);

  const [uploadedFileForScreen, setUploadedFileForScreen] = useState<File | undefined>(undefined);
  const [tempImageURL, setTempImageURL] = useState<string>("");
  const [openFileSizeExceededMessage, setOpenFileSizeExceededMessage] = useState<boolean>(false);
  const [showDistance, setShowDistance] = useState<boolean>(values.show_distance ?? true);
  const [deleteFaceImage, setDeleteFaceImage] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [openImageUpload, setOpenImageUpload] = useState<boolean>(false);

  const cropperInputFileRef = useRef<File | undefined>(undefined);
  const cropperInputURLRef = useRef<string>("");

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);


  const dropzoneForScreen = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.svg'],
    },
    maxFiles: 1,
  });

  useEffect(() => {

    setFieldValue("show_distance", true);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (categoriesGroupState) {
      const category = categoriesGroupState.categories?.find(
        (cat) => cat.Name === values.category);
      if (category) {
        setSelectedCategory(category);
        onCategorySelect(category);
      }
    }

  }, [categoriesGroupState]);

  useEffect(() => {
    if (dropzoneForScreen.acceptedFiles && dropzoneForScreen.acceptedFiles.length >= 1) {
      const file = dropzoneForScreen.acceptedFiles[0];
      // Check image dimensions to make sure its 256 x 256
      getImageDimensions(file, (width, height) => {
        if (width == 256 && height == 256) {
          const imageUrl = URL.createObjectURL(file);
          setUploadedFileForScreen(file);
          handleFileChange(file);
          setTempImageURL(imageUrl);
        }
        else {
          setOpenFileSizeExceededMessage(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropzoneForScreen.acceptedFiles]);

  const onDeleteUploadFileForScreen = () => {
    dropzoneForScreen.acceptedFiles.splice(0,1);
    setUploadedFileForScreen(undefined);
    handleFileChange(undefined);
    setDeleteFaceImage(true);
    setTempImageURL("");

    if(values.has_custom_beacon_face){
      handleDeleteFace(true);
    }
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputFile = event.target.files![0];


    getImageDimensions(inputFile, (width, height) => {
      if (width == 256 && height == 256) {
        const imageUrl = URL.createObjectURL(inputFile);
        setUploadedFileForScreen(inputFile);
        handleFileChange(inputFile);
        setTempImageURL(imageUrl);

        cropperInputFileRef.current = inputFile;
        cropperInputURLRef.current = imageUrl;

      } else {
        resizeImage(inputFile, 256, 256, AppConstants.MaxImageBytes, (resizedFile?: File) => {
          if (resizedFile) {
            const imageUrl = URL.createObjectURL(resizedFile);
            setUploadedFileForScreen(resizedFile);
            handleFileChange(resizedFile);
            setTempImageURL(imageUrl);

            cropperInputFileRef.current = resizedFile;
            cropperInputURLRef.current = imageUrl;

          } else {
            setOpenFileSizeExceededMessage(true);
          }
        });
      }
    });

    resetCropAndZoom();
    setOpenImageUpload(true);

    event.target.value = "";
  };  

  const handleDistanceCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setShowDistance(checked);
    setFieldValue("show_distance", checked);
    handleChange(event);
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    onCategorySelect(category);
    setFieldValue("category", category.Name);
  };

  const handleModelSelect = (model: string) => {
    setFieldValue("model", model);
  };

  const editOriginalImage = () => {

  if (tempImageURL && tempImageURL !== cropperInputURLRef.current) {
    URL.revokeObjectURL(tempImageURL);
  }

    setUploadedFileForScreen(cropperInputFileRef.current);
    setTempImageURL(cropperInputURLRef.current);
    handleFileChange(cropperInputFileRef.current);

    setOpenImageUpload(true);
  };

  const resetCropAndZoom = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <Form style={{ width: "100%", marginBottom: 8, marginTop: 8 }}>
      <Box sx={{ mr: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 2 }}>Category</Typography>
        <CategoryDropdown 
          onCategorySelect={handleCategorySelect}
          errors={errors}
          touched={touched}
          initalCategory={selectedCategory}
        />
        {/* <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Beacon Type</Typography>
        <Box sx={{ display: "flex", gap: 10 }}>
        <HorizontalMenu 
          onModelSelect={handleModelSelect}
          model={values.model}
        />
        <FormControlLabel control={<Checkbox checked={showDistance} onChange={handleDistanceCheckboxChange}/>} label="Display Distance" />
        </Box> */}
        <Divider sx={{ my: 4 }} />  
        <Box sx={{marginTop: 6}}>
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Icon</Typography>
          <Box sx={{ display: "flex", gap: 10, marginTop: 1, padding: 2 }}>
              <Box
                onClick={tempImageURL && cropperInputFileRef ? () => editOriginalImage() : undefined}
                sx={{
                  border: 'dashed 2px silver',
                  borderRadius: 2.5,
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  backgroundColor: `#${selectedCategory?.Color}`,
                  alignContent: 'center',
                  width: 128,
                  height: 128,
                  position: 'relative',
                  cursor: tempImageURL && cropperInputFileRef ? "pointer" : "default" 
                }}
              >
                {imageId || tempImageURL ? (
                  <>
                    <CardMedia
                    sx={{ width: "100%", height: "100%", objectFit: "fill", overflow: "hidden" }}
                    component="img"
                    src={tempImageURL ? tempImageURL : getImageURLOnServerBeaconFace(imageId ?? "", "")}
                    alt="beacon"
                  />
                  {tempImageURL && cropperInputFileRef && (
                    <IconButton 
                      size="small" 
                      sx={{ 
                        position: "absolute",
                        top: 0,
                        right: 0,
                        padding: "4px",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        color: "white",
                        '&:hover': {
                          backgroundColor: "rgba(0,0,0,0.5)",
                        },
                      }}
                    >
                        <EditIcon sx={{ color: "white" }} />
                    </IconButton>
                  )}
                  </>
                ) : (
                  <>
                  {selectedCategory?.Id ? (
                    <ImageLoader 
                      imageURL={getImageURLCategoryListIcon(selectedCategory.Id, selectedCategory.LastModified)}
                    />
                  ) : (
                    <ImageOutlinedIcon
                    style={{
                      fontSize: 40,
                      color: 'silver',
                    }}
                  />
                )}
                </>
                )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "end", mb: 1 }}>
            <label htmlFor="file-input-face">
            <Button
              component="span"
              variant="outlined"
              sx={{
                whiteSpace: "nowrap",
                height: 40,
              }}>
                Upload Custom Image
            </Button>
            </label>
            <input
              id="file-input-face"
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              style={{ display: 'none' }}
              onChange={handleFilterChange}
            />
            </Box>
          </Box>
          {(uploadedFileForScreen || (!deleteFaceImage && imageId != undefined)) &&
            <Box sx={{ paddingX: 2 }}>
              <FileUploadPreview
                file={uploadedFileForScreen}
                imageId={imageId}
                onDeleteUploadFile={onDeleteUploadFileForScreen}
              />
            </Box>}
        </Box>
      </Box>
        <UploadImageModal 
          open={openImageUpload}
          onConfirm={() => {
            setOpenImageUpload(false);
          }}
          onDeny={() => {
            setOpenImageUpload(false);

            if (tempImageURL) {
              URL.revokeObjectURL(tempImageURL);
            }
            handleFileChange(undefined);
            setTempImageURL("");
            setUploadedFileForScreen(undefined);
            resetCropAndZoom();
          }}
          confirmButtonTitle={"Save"}
          denyButtonTitle={"Cancel"}
          setCurrentAssetFile={(croppedFile) => {
            handleFileChange(croppedFile);
          }}
          setCurrentImageURL={setTempImageURL}
          imageFile={uploadedFileForScreen}
          imageURL={tempImageURL}
          beaconModel={categoriesGroupState.categories?.find(x => x.Name == values.category)?.Theme}
          paddingValue={0.05}
          crop={crop}
          setCrop={setCrop}
          zoom={zoom}
          setZoom={setZoom}
        />
      <AppConfirmDialog
        open={openFileSizeExceededMessage}
        onConfirm={() => setOpenFileSizeExceededMessage(false)}
        confirmButtonTitle="OK"
        disableDenyButton={true}
        title={`The image is too large. Images have a maximum filesize of ${convertByteString(AppConstants.MaxImageBytes)}. Please try resizing the image then uploading it again.`}
        dialogTitle="Problem attaching image to beacon"
        content=""
      />
    </Form>
  );
};

export default BeaconForm;
